import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { BaseComponent } from '@premotec/pmt-essentials';

import { environment } from '../../../../environments/environment';
import { HeaderStateService } from '../../../main/headers/services/header-state.service';
import { AssignmentDetailsReturnLocationEnum } from '../../../shared/enums/assignment-details-return-location.enum';
import { SharedToasterService } from '../../../shared/services/shared-toaster.service';
import { AssignmentDetailsDateModel } from './models/assignment-details-date.model';
import { AssignmentDetailsStateService } from './services/assignment-details-state.service';
import { AssignmentDetailsService } from './services/assignment-details.service';
import { AssignmentParticipantSkillsService } from './services/assignment-participant-skills.service';
import { AssignmentParticipantsAttendanceService } from './services/assignment-participants-attendance.service';
import { AssignmentParticipantsStateService } from './services/assignments-participants-state.service';

@Component({
    selector: 'app-assigment-details',
    templateUrl: './assignment-details.component.html',
    styleUrl: './assignment-details.component.scss'
})
export class AssigmentDetailsComponent extends BaseComponent implements OnInit {
    assignmentId!: number;
    isSkillAllocationAllowed: boolean = false;
    isAttendanceCheckAllowed: boolean = false;
    informationLink: string | null = null;

    // changeable return url with default
    private returnUrl: string = '/assignments/my';

    assignmentDates: AssignmentDetailsDateModel[] | null = null;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private headerStateService: HeaderStateService,
        private assignmentDetailsService: AssignmentDetailsService,
        private assignmentParticipantSkillService: AssignmentParticipantSkillsService,
        private assignmentParticipantsAttendanceService: AssignmentParticipantsAttendanceService,
        private assignmentParticipantStateService: AssignmentParticipantsStateService,
        protected assignmentDetailsStateService: AssignmentDetailsStateService,
        private sharedToasterService: SharedToasterService,
        private translocoService: TranslocoService
    ) {
        super();
        this.setReturnUrl();
        this.setHeaderInformation();
    }

    ngOnInit(): void {
        this.registerEvents();
        this.route.params.subscribe(params => {
            this.assignmentId = params['id'];
            this.assignmentDetailsStateService.loadAssingmentDetails(this.assignmentId);
            this.assignmentParticipantStateService.loadAssignmentParticipants(this.assignmentId);
            this.loadAssignmentDates();
            this.loadIsSkillAllocationAllowed();
            this.loadIsAttendanceCheckAllowed();
        });
    }

    private setReturnUrl(): void {
        let returnLocation = this.route.snapshot.queryParams['returnLocation'];
        if (returnLocation == null) return;

        switch (returnLocation.toLowerCase()) {
            case AssignmentDetailsReturnLocationEnum.Past:
                this.returnUrl = '/assignments/past';
                break;
            default:
                this.returnUrl = '/assignments/my';
        }
    }

    private setOnlineDeskUrl(eventLessonId: number, isLesson: boolean): void {
        const eventLessonUrl = isLesson ? 'lessons' : 'events';
        this.informationLink = `${environment.onlineDeskUrl}/${eventLessonUrl}/${eventLessonId}`
    }

    private resetOnlineDeskUrl(): void {
        this.informationLink = null;
    }

    private setHeaderInformation(): void {
        let onlineDeskUrlIcon = (this.informationLink) ? 'fa-solid fa-arrow-up-right-from-square text-primary' : 'fa-solid fa-spinner fa-spin';

        this.headerStateService.setDetailHeader(
            this.translocoService.translate('main.header.assignmentDetailsTitle'),
            this.translocoService.translate('main.header.assignmentDetailsDescription'),
            this.returnUrl,
            onlineDeskUrlIcon);
    }

    private registerEvents(): void {
        this.assignmentDetailsStateService.assignmentDetails.subscribe((x) => {
            this.resetOnlineDeskUrl();
            if (x == null) return;

            this.setOnlineDeskUrl(x.parentEventLessonId ?? x.eventLessonId, x.isLesson);
            this.setHeaderInformation();
        });

        // Header icon action
        this.headerStateService.getActionClickedEvent().subscribe((x) => {
            // Navigate to link logic
            if (!this.informationLink) return;
            window.open(this.informationLink!, '_blank');
        });
    }

    private loadAssignmentDates(): void {
        this.assignmentDetailsService.getAssignmentDetailDates(this.assignmentId).subscribe({
            next: (dates) => {
                this.assignmentDates = dates;
            },
            error: (error) => {
                this.sharedToasterService.showGeneralDataLoadingError();
            }
        });
    }

    private loadIsSkillAllocationAllowed(): void {
        this.assignmentParticipantSkillService.getIsSkillAllocationAllowed(this.assignmentId).subscribe({
            next: (isAllowed) => {
                this.isSkillAllocationAllowed = isAllowed;
            },
            error: (error) => {
                this.sharedToasterService.showGeneralDataLoadingError();
            }
        });
    }

    private loadIsAttendanceCheckAllowed(): void {
        this.assignmentParticipantsAttendanceService.getIsCheckAllowed(this.assignmentId).subscribe({
            next: (isAllowed) => {
                this.isAttendanceCheckAllowed = isAllowed;
            },
            error: (error) => {
                this.sharedToasterService.showGeneralDataLoadingError();
            }
        });
    }

    protected navigateBack(): void {
        this.router.navigate([this.returnUrl]);
    }
}
